import ChatFloatingWidget from '@/components/chat/ChatFloatingWidget';
import BlockCustomerDialog from '@/components/customers/BlockCustomerDialog';
import MyContainer from '@/components/MyContainer';
import { Button } from '@/components/ui/button';
import { routes } from '@/routes/route-constants';
import { useCustomer } from '@/services/customer-service';
import { Ban, Edit, History, ShieldCheck } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useLocation, useParams } from 'wouter';
import OrderAsyncTable from '../order/component/order-async-table';
import { CustomerDetailError } from './components/customer-detail-error';
import { CustomerDetailLoading } from './components/customer-detail-loading';
import { CustomerInfoCard } from './components/customer-info-card';
import { RecentOrderCard } from './components/recent-order-card';
import { useCustomerChat } from './hooks/use-customer-chat';

export default function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const [chatOpen, setChatOpen] = useState(false);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockDialogTab, setBlockDialogTab] = useState<'action' | 'history'>('action');

  const { data: customer, isLoading, error, refresh: refetch } = useCustomer(id);
  const recentOrder = customer?.recentOrder;

  const {
    canChat,
    currentConversation,
    messages,
    isLoadingMessage,
    prevCursor,
    onNewMessage,
    onLoadMoreMessage,
  } = useCustomerChat(customer);

  const handleBack = useCallback(() => setLocation('/customers'), [setLocation]);
  const handleEdit = useCallback(() => setLocation(routes.customer.edit(id)), [setLocation, id]);
  const handleViewOrder = useCallback(
    (orderId: string) => setLocation(`/orders/${orderId}`),
    [setLocation],
  );

  const handleRetry = useCallback(() => window.location.reload(), []);

  if (isLoading) {
    return <CustomerDetailLoading />;
  }

  if (error || !customer) {
    return (
      <CustomerDetailError
        error={error || "We couldn't load the customer details."}
        onRetry={handleRetry}
        onBack={handleBack}
      />
    );
  }

  return (
    <>
      <MyContainer
        title={customer.name}
        subtitle="Customer details and order history"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => {
                setBlockDialogTab('history');
                setBlockDialogOpen(true);
              }}
            >
              <History className="w-4 h-4" /> Block History
            </Button>
            {customer.blocked ? (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                onClick={() => {
                  setBlockDialogTab('action');
                  setBlockDialogOpen(true);
                }}
              >
                <ShieldCheck className="w-4 h-4" /> Unblock
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                onClick={() => {
                  setBlockDialogTab('action');
                  setBlockDialogOpen(true);
                }}
              >
                <Ban className="w-4 h-4" /> Block Customer
              </Button>
            )}
            <Button variant="outline" size="sm" className="gap-2" onClick={handleEdit}>
              <Edit className="w-4 h-4" /> Edit
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
             <OrderAsyncTable customerId={id!}/>
          </div>

          <div className="space-y-6">
            <CustomerInfoCard
              customer={customer}
              onCustomerUpdated={() => refetch()}
            />
            <RecentOrderCard order={recentOrder} onViewOrder={handleViewOrder} />
          </div>
        </div>
      </MyContainer>

      <BlockCustomerDialog
        customer={customer}
        open={blockDialogOpen}
        onOpenChange={setBlockDialogOpen}
        defaultTab={blockDialogTab}
        onSuccess={() => refetch()}
      />

      {/* Chat Floating Widget */}
      {canChat && currentConversation && (
        <ChatFloatingWidget
          currentConversation={currentConversation}
          messages={messages}
          isLoadingMessage={isLoadingMessage}
          prevCursor={prevCursor ?? ''}
          OnNewMessage={onNewMessage}
          OnLoadMoreMessage={onLoadMoreMessage}
          open={chatOpen}
          onOpenChange={setChatOpen}
          title={customer.name}
        />
      )}
    </>
  );
}

